import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { InquiryForm, LayoutItem } from '../../../models/Inquiry/Inquiry';
import config from '../../../config';
import { decrypt } from '../../../utils/encryption';

const Paginator = () => (
  <div className="d-flex align-items-center justify-content-center flex-wrap">
    <div className="flex-fill">
      <div className="d-flex justify-content-between align-items-center mt-2">
        <div>
          1-1 of 1
        </div>

        <div className="d-flex align-items-center flex-row gap-2">
          <select className="form-control form-select">
            <option value="1">10</option>
            <option value="2">25</option>
            <option value="3">50</option>
          </select>

          <ul className="justify-content-center pagination pagination-md mb-0">
            <li className="page-item disabled">
              <span className="page-link">
                <span aria-hidden="true">‹</span>
                <span className="visually-hidden">
                  Previous
                </span>
              </span>
            </li>
            <li className="page-item active">
              <span className="page-link">
                1
                <span className="visually-hidden">
                  (current)
                </span>
              </span>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                <span aria-hidden="true">›</span>
                <span className="visually-hidden">
                  Next
                </span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

interface LayoutItemPreview extends LayoutItem {
  panel: number;
}

const InquiryPreviewList = () => {
  const [data, setData] = useState<InquiryForm>();
  const [sortedList, setSortedList] = useState<LayoutItemPreview[][]>([]);

  useEffect(() => {
    try {
      const encrypted = window.sessionStorage.getItem(config.preview.storageKey);
      const dataString = encrypted && decrypt(encrypted);
      const json = dataString && JSON.parse(dataString);

      if (json) setData(json);
    } catch (error) {
      console.warn(error);
    }
  }, []);

  useEffect(() => {
    // Sorted Items structure:
    // LayoutItem[row][item]
    //
    // Data structure:
    // LayoutItem[panel][item]
    //
    // Insert the items into the sortedList based on the layout on webdb list_layout
    if (data?.list_layout) {
      const ItemsInLeftPanel = data.list_layout[0];
      const ItemsInRightPanel = data.list_layout[1];

      if (ItemsInLeftPanel || ItemsInRightPanel) {
        // Verify each item in the left panel and for its corresponding item in the right panel
        // and add them into a single row in the sortedList
        const maxLength = Math.max(ItemsInLeftPanel.length, ItemsInRightPanel.length);
        const newSortedList: LayoutItemPreview[][] = [];

        for (let i = 0; i < maxLength; i++) {
          const row: LayoutItemPreview[] = [];
          if (ItemsInLeftPanel[i]) {
            row.push({
              ...ItemsInLeftPanel[i],
              panel: 0, // Left panel
            });
          }

          if (ItemsInRightPanel[i]) {
            row.push({
              ...ItemsInRightPanel[i],
              panel: 1, // Right panel
            });
          }

          newSortedList.push(row);
        }

        setSortedList(newSortedList);
      }
    }
  }, [data?.list_layout]);

  const renderCell = (item: LayoutItemPreview, rowIndex: number) => {
    const hasSibling = sortedList[rowIndex]?.length > 1;
    const isRightPanel = item?.panel === 1;
    const isBlank = item.uuid.startsWith('empty_');

    if (isBlank) {
      if (!hasSibling) {
        return (
          <td
            key={uuid()}
            colSpan={4}
            style={{ border: '1px solid #ccc' }}
          />
        );
      }

      return (
        <td
          key={uuid()}
          colSpan={2}
          style={{ border: 'none' }}
        />
      );
    }

    return (
      <>
        <th
          translate="no"
          className="notranslate"
          style={{
            width: '20%',
            verticalAlign: 'top',
            border: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            backgroundColor: '#eee',
            fontWeight: '400',
            padding: '4px 8px',
          }}
        >
          {item.label}
        </th>

        <td
          colSpan={(!hasSibling && !isRightPanel) ? 3 : 1}
          style={{
            border: '1px solid #ccc',
          }}
        />
      </>
    );
  };

  return (
    <main style={{ overflowY: 'auto' }}>
      <div className="container py-4">
        <div className="d-flex flex-column align-items-center gap-4">
          <h1 className="h1 fw-bold notranslate" translate="no">{data?.name}</h1>
          <p translate="no" className="notranslate">{data?.description}</p>
        </div>

        <Paginator />

        <table
          className="table"
          style={{
            width: '100%',
            margin: '20px auto 0',
            lineHeight: '1.5',
            fontSize: '.9rem',
            captionSide: 'bottom',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <tbody>
            {sortedList.map((row, rowIndex) => (
              <tr key={uuid()}>
                {row.map((item) => renderCell(item, rowIndex))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="w-100 text-center mt-2">
          <button type="button" className="btn btn-primary btn-sm px-3 py-1">
            View details
          </button>
        </div>

        <Paginator />
      </div>
    </main>
  );
};

export default InquiryPreviewList;
